import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Gift, Loader2, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const PLAN_LABEL = {
  parentia_solo_monthly: "Solo · 1 mois",
  parentia_solo_yearly: "Solo · 1 an",
  parentia_famille_monthly: "Famille+ · 1 mois",
};

export default function GiftDialog({ open, onOpenChange, lookupKey }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [deliverAt, setDeliverAt] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setEmail("");
      setMessage("");
      setDeliverAt("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        lookup_key: lookupKey,
        recipient_name: name.trim(),
        recipient_email: email.trim(),
        message: message.trim(),
        deliver_at: deliverAt ? new Date(deliverAt).toISOString() : null,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/gifts/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur au paiement");
      setBusy(false);
    }
  };

  const minDate = new Date().toISOString().slice(0, 16);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="gift-dialog" className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-[#E8899F]" />
            Offrir {PLAN_LABEL[lookupKey] || "un abonnement"}
          </DialogTitle>
          <DialogDescription>
            Renseigne les infos du destinataire. Tu recevras un code à partager.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Prénom du destinataire</Label>
            <Input data-testid="gift-name-input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Camille" />
          </div>
          <div>
            <Label>Email du destinataire</Label>
            <Input data-testid="gift-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="camille@exemple.fr" />
          </div>
          <div>
            <Label>Message personnalisé (optionnel)</Label>
            <Textarea data-testid="gift-message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Bonne fête des mères 💛" maxLength={500} />
          </div>
          <div>
            <Label className="flex items-center gap-1">
              <CalendarClock className="h-4 w-4 text-[#23375E]" /> Date de livraison (optionnel)
            </Label>
            <Input data-testid="gift-deliver-input" type="datetime-local" value={deliverAt} min={minDate} onChange={(e) => setDeliverAt(e.target.value)} />
            <p className="mt-1 text-xs text-[#77849C]">
              Laisse vide pour une livraison immédiate après paiement.
            </p>
          </div>
          <Button data-testid="gift-submit-btn" type="submit" disabled={busy} className="w-full brand-btn rounded-full py-5 font-semibold">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirection...</> : "Continuer vers le paiement"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
